import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { Message } from "../types/tutor.types";

export interface SocratesChatStoreState {
  // 상태값
  history: Message[];
  fetching: boolean;
  pieceDataSum: string;
  isStartInterview: boolean;
  isAddingMode: boolean;

  // 메시지 액션
  addMessage: (message: Message) => void;
  addMessages: (messages: Message[]) => void;
  updateMessage: (messageId: string, updates: Partial<Message>) => void;
  setHistory: (history: Message[]) => void;
  clearHistory: () => void;

  // 스트리밍 액션
  startStreamingMessage: (messageId: string, groupId: string) => void;
  appendStreamChunk: (messageId: string, chunk: string) => void;
  completeStreamingMessage: (messageId: string) => void;
  abortStreamingMessage: (messageId: string, error?: string) => void;

  // 소크라테스 전용 액션
  setIsStartInterview: (isStartInterview: boolean) => void;
  setIsAddingMode: (isAddingMode: boolean) => void;

  // 기타 액션
  setFetching: (fetching: boolean) => void;
  setPieceDataSum: (data: string) => void;
}

/**
 * 소크라테스 채팅 스토어
 * - 소크라테스 모드의 채팅 메시지 관리
 * - 면접 시뮬레이션 상태 관리
 * - 스트리밍 응답 처리
 */
export const useSocratesChatStore = create<SocratesChatStoreState>()(
  devtools(
    (set) => ({
      // ============================================
      // 초기 상태
      // ============================================
      history: [],
      fetching: false,
      pieceDataSum: "",
      isStartInterview: false,
      isAddingMode: false,

      // ============================================
      // 메시지 관리 액션
      // ============================================

      // 단일 메시지 추가
      addMessage: (message: Message) => {
        set((state) => ({
          history: [...state.history, message],
        }));
      },

      // 여러 메시지 추가
      addMessages: (messages: Message[]) => {
        set((state) => ({
          history: [...state.history, ...messages],
        }));
      },

      // 특정 메시지 업데이트
      updateMessage: (messageId: string, updates: Partial<Message>) => {
        set((state) => ({
          history: state.history.map((msg) =>
            msg.id === messageId ? { ...msg, ...updates } : msg
          ),
        }));
      },

      // 히스토리 전체 교체
      setHistory: (history: Message[]) => set({ history }),

      // 히스토리 초기화
      clearHistory: () => set({ history: [] }),

      // ============================================
      // 스트리밍 전용 액션
      // ============================================

      /**
       * 스트리밍 메시지 시작
       * - AI 메시지를 빈 내용으로 생성하고 is_loading = true로 설정
       */
      startStreamingMessage: (messageId: string, groupId: string) => {
        const newMessage: Message = {
          id: messageId,
          group_id: groupId,
          content: "",
          sender: "ai",
          created_at: new Date().toISOString(),
          is_loading: true,
        };

        set((state) => ({
          history: [...state.history, newMessage],
          fetching: true,
          pieceDataSum: "", // 청크 누적용 초기화
        }));
      },

      /**
       * 스트리밍 청크 추가
       * - 해당 메시지의 content에 청크를 추가
       * - pieceDataSum에도 누적 (레거시 호환성)
       */
      appendStreamChunk: (messageId: string, chunk: string) => {
        set((state) => {
          const updatedHistory = state.history.map((msg) =>
            msg.id === messageId
              ? { ...msg, content: msg.content + chunk }
              : msg
          );

          return {
            history: updatedHistory,
            pieceDataSum: state.pieceDataSum + chunk,
          };
        });
      },

      /**
       * 스트리밍 완료
       * - is_loading을 false로 변경
       * - fetching 상태 해제
       */
      completeStreamingMessage: (messageId: string) => {
        set((state) => ({
          history: state.history.map((msg) =>
            msg.id === messageId ? { ...msg, is_loading: false } : msg
          ),
          fetching: false,
        }));
      },

      /**
       * 스트리밍 중단/실패
       * - error 메시지 설정
       * - is_loading을 false로 변경
       * - fetching 상태 해제
       */
      abortStreamingMessage: (messageId: string, error?: string) => {
        set((state) => ({
          history: state.history.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  is_loading: false,
                  error: error || "응답 생성 중 문제가 발생했습니다.",
                }
              : msg
          ),
          fetching: false,
        }));
      },

      // ============================================
      // 소크라테스 전용 액션
      // ============================================

      // 면접 시작 상태 설정
      setIsStartInterview: (isStartInterview: boolean) =>
        set({ isStartInterview }),

      // 커스텀 폼 추가 모드 설정
      setIsAddingMode: (isAddingMode: boolean) => set({ isAddingMode }),

      // ============================================
      // 기타 액션
      // ============================================

      // 통신 상태 업데이트
      setFetching: (fetching: boolean) => set({ fetching }),

      // 스트리밍 데이터 누적 (레거시 호환성)
      setPieceDataSum: (data: string) => set({ pieceDataSum: data }),
    }),
    { name: "SocratesChatStore" } // Redux DevTools 이름
  )
);
